type AuthSource = "local" | "remote";
type AuthMessageType =
  | { type: "signin"; externalId?: string }
  | { type: "signout" };

const AUTH_STORAGE_KEY = "app:auth:storage";
const AUTH_EXTERNAL_ID_KEY = "app:auth:external-id";

/**
 * AuthService —— 登录状态管理服务
 *
 * 职责：
 * 1. 集中维护登录状态，作为全站“真相来源”
 * 2. 利用 sessionStorage 实现会话级持久化
 * 3. 利用 BroadcastChannel 实现跨标签页实时同步
 * 4. 通过 Unpoly 事件系统通知其他组件
 */
export type AuthState = {
  signedIn: boolean;
  externalId: string | null;
};

export class AuthService {
  private static instance: AuthService;
  private _signedIn: boolean = false;
  private _externalId: string | null = null;
  private channel: BroadcastChannel;
  private stateListeners = new Set<(state: AuthState) => void>();

  /**
   * 获取单例实例
   */
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private constructor() {
    up.on("auth:signout", async (event: any) => {
      console.info("[AuthService] 收到 auth:signout 事件, 事件对象:", event);
      this.syncAuthStatus(false, "local", null);
    });

    up.on("auth:signin", (event: any) => {
      console.info("[AuthService] 收到 auth:signin 事件, 事件对象:", event);
      // event.userSlug 由 X-Up-Accept-Layer 透传，无刷新登录时是 PushService 等
      // 下游订阅者拿到 externalId 的唯一途径——这里把它锁进真源
      const slug = (event.userSlug as string | undefined) ?? null;
      this.signedIn(slug).then();
    });

    this.channel = new BroadcastChannel("app:auth:channel");
    console.info("[AuthService] 初始化，已连接 BroadcastChannel");
    // 监听来自其他标签页的同步消息
    this.channel.onmessage = (event: MessageEvent<AuthMessageType>) => {
      console.info("[AuthService] 收到同步消息:", event.data);
      if (event.data.type === "signin") {
        this.syncAuthStatus(true, "remote", event.data.externalId ?? null);
      } else if (event.data.type === "signout") {
        this.syncAuthStatus(false, "remote", null);
      }
    };
    // 初始从本地存储读取
    this.fromStorage();
  }

  /**
   * 初始从 sessionStorage 加载状态
   */
  private fromStorage(): void {
    const stored = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (stored !== null) {
      this._signedIn = stored === "true";
      console.info("[AuthService] 从 sessionStorage 加载状态:", this._signedIn);
    }
    const storedId = sessionStorage.getItem(AUTH_EXTERNAL_ID_KEY);
    if (storedId) {
      this._externalId = storedId;
      console.info(
        "[AuthService] 从 sessionStorage 加载 externalId:",
        storedId,
      );
    }
  }

  /**
   * 从 DOM 初始化（作为初始挂载时的二次校验）
   * 建议在 DOMContentLoaded 或编译器初始化阶段调用
   *
   * body.dataset.signedIn 由 SSR 写入，承载用户 slug；空串视为未登录。
   * 这是首次进入页面时 externalId 的来源（无 sessionStorage、无 signin 事件）。
   */
  fromDOM(): void {
    const raw = document.body.dataset.signedIn;
    const slug = raw && raw.length > 0 ? raw : null;
    const signedIn = slug !== null;
    console.info(
      "[AuthService] 从 DOM 读取状态:",
      signedIn,
      "externalId:",
      slug,
    );
    if (this._signedIn !== signedIn || this._externalId !== slug) {
      console.info("[AuthService] DOM 状态与内存状态不一致，正在同步...");
      this.syncAuthStatus(signedIn, "local", slug);
    }
  }

  /**
   * 获取当前登录状态
   */
  get isSignedIn(): boolean {
    return this._signedIn;
  }

  /**
   * 当前登录用户的 externalId（usersTable.slug），未登录或后端未透传时为 null。
   * 供 PushService 等下游订阅者读取，避免重复访问 body.dataset.signedIn。
   */
  get externalId(): string | null {
    return this._externalId;
  }

  /**
   * 订阅登录态变更（覆盖本地触发 + 跨标签页同步两路）。
   * PushService 等下游用这个统一通道同步 SDK 状态，
   * 避免依赖 up.on 事件（远端 BroadcastChannel 同步不会经 up 系统）。
   */
  onStateChange(cb: (state: AuthState) => void): () => void {
    this.stateListeners.add(cb);
    return () => {
      this.stateListeners.delete(cb);
    };
  }

  /**
   * 触发登录成功状态。
   * @param externalId 用户 slug；不传则保留当前 id（auth:signin 事件无 userSlug 字段时的安全行为）。
   */
  async signedIn(externalId?: string | null): Promise<void> {
    this.reloadUI().then();
    const id = externalId !== undefined ? externalId : this._externalId;
    this.syncAuthStatus(true, "local", id);
  }

  private async reloadUI(): Promise<void> {
    console.info("[AuthService] 重载 #header-user, #sidebar-nav");
    await up.reload("#header-user, #sidebar-nav", { layer: "root" });
  }

  /**
   * 触发登出状态
   */
  async signedOut(): Promise<void> {
    this.syncAuthStatus(false, "local", null);
  }

  /**
   * 核心状态切换逻辑
   * @param value 新的登录状态
   * @param source 变更来源（local: 本页触发, remote: 其他标签页同步驱动）
   * @param externalId 登录后的用户 slug；登出传 null
   */
  private syncAuthStatus(
    value: boolean,
    source: AuthSource,
    externalId: string | null,
  ): void {
    console.info(
      `[AuthService] 状态同步: ${this._signedIn} -> ${value}, externalId: ${this._externalId} -> ${externalId}, 来源: ${source}`,
    );
    const newId = value ? externalId : null;
    const hasChanged = this._signedIn !== value || this._externalId !== newId;
    this._signedIn = value;
    this._externalId = newId;
    sessionStorage.setItem(AUTH_STORAGE_KEY, String(value));
    if (newId) {
      sessionStorage.setItem(AUTH_EXTERNAL_ID_KEY, newId);
    } else {
      sessionStorage.removeItem(AUTH_EXTERNAL_ID_KEY);
    }
    if (hasChanged && source === "local") {
      // 如果是本页主动触发的变更，则广播给其他标签页
      console.info("[AuthService] 正在广播状态变更...");
      this.channel.postMessage(
        value
          ? { type: "signin", externalId: newId ?? undefined }
          : { type: "signout" },
      );
    }
    // 通知本进程订阅者（local + remote 都触发），让 PushService 等
    // 在远端 BroadcastChannel 驱动的同步下也能拿到状态变化
    if (hasChanged) {
      const snapshot: AuthState = {
        signedIn: this._signedIn,
        externalId: this._externalId,
      };
      this.stateListeners.forEach((cb) => {
        cb(snapshot);
      });
    }
  }

  signin(options: Pick<up.LayerOptions, "onAccepted" | "onDismissed">) {
    up.layer.open({
      url: `${(window as any).__LANG_PREFIX__ || "/zh"}/web/auth/signin?redirect=false`,
      target: "#auth",
      mode: "modal",
      size: "small",
      onAccepted: async (event: up.LayerAcceptedEvent) => {
        console.log("[AuthService] 登录成功");
        this.signedIn().then();
        options.onAccepted?.(event);
      },
      ...options,
    });
  }
}

// 导出单例以便直接使用
export const authService = AuthService.getInstance();
